/*
 * The current organisation in the top bar. With more than one membership it opens a menu to switch
 * (the session moves to that organisation and every query reloads).
 */
import { CaretUpDown, Check } from '@phosphor-icons/react'
import { DropdownMenu as M } from 'radix-ui'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useAuth, useSwitchOrganization } from '@/api/hooks/auth'
import { menuContentClasses, menuItemClasses } from '@/components/ui'
import { cn } from '@/lib/cn'

function OrgTile({ name }: { name: string }) {
  return (
    <span
      aria-hidden="true"
      className="grid size-7 shrink-0 place-items-center rounded-lg bg-[linear-gradient(140deg,var(--color-brand-500),var(--color-brand-700))] text-12 font-bold text-white shadow-sm"
    >
      {Array.from(name.trim())[0]?.toUpperCase()}
    </span>
  )
}

export function OrgSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation('shell')
  const session = useAuth()
  const navigate = useNavigate()
  const sw = useSwitchOrganization()
  const org = session.organization
  const multi = session.memberships.length > 1
  const label = (
    <>
      <OrgTile name={org.name} />
      <span className="flex min-w-0 flex-col text-start leading-tight">
        <span className="truncate text-14 font-semibold text-ink">{org.name}</span>
        <span className="truncate text-12 text-subtle">{t(`roles.${session.role}`, { ns: 'common' })}</span>
      </span>
    </>
  )
  if (!multi) return <div className={cn('flex min-w-0 items-center gap-2.5 px-1', className)}>{label}</div>
  return (
    <M.Root modal={false}>
      <M.Trigger
        className={cn(
          'flex min-w-0 items-center gap-2.5 rounded-xl px-1.5 py-1 transition-colors hover:bg-control data-[state=open]:bg-control',
          className,
        )}
        aria-label={t('org.switch')}
      >
        {label}
        <CaretUpDown className="size-4 shrink-0 text-subtle" aria-hidden="true" />
      </M.Trigger>
      <M.Portal>
        <M.Content align="start" sideOffset={6} className={menuContentClasses}>
          <M.Label className="px-2.5 pt-2 pb-1 text-12 font-semibold text-subtle">{t('org.title')}</M.Label>
          {session.memberships.map((m) => (
            <M.Item
              key={m.organizationId}
              className={menuItemClasses}
              onSelect={() =>
                m.organizationId !== org.id &&
                sw.mutate(m.organizationId, { onSuccess: () => navigate('/', { replace: true }) })
              }
            >
              <OrgTile name={m.organizationName} />
              <span className="flex-1 truncate">{m.organizationName}</span>
              {m.organizationId === org.id ? <Check className="size-4 text-brand-600" weight="bold" /> : null}
            </M.Item>
          ))}
        </M.Content>
      </M.Portal>
    </M.Root>
  )
}
